/**
 * A document's text, rendered from its Markdown subset: a legal document, or —
 * in the `guide` dialect, with code and tables — one of the product's docs.
 *
 * Every element is built by React from parsed data (`lib/markdown.ts`), so the
 * document is text, never markup: nothing an author types can add a script, a
 * form or a tracking pixel to the page a client reads and agrees to.
 *
 * Used by the admin portal's preview and the client app's reader, so what the
 * author previews is exactly what the client is shown.
 *
 * Keys are positions: the blocks are derived wholly from the text and rebuilt
 * whenever it changes, so a position is exactly their identity.
 */

import { Fragment, useMemo } from 'react';

import { cn } from '../../lib/cn';
import { parseDocument, type Align, type Block, type Dialect, type Inline } from '../../lib/markdown';
import { ScrollArea } from '../ui/scroll-area';

function InlineText({ nodes }: { nodes: Inline[] }) {
  return (
    <>
      {nodes.map((node, index) => {
        switch (node.kind) {
          case 'text':
            return <Fragment key={index}>{node.text}</Fragment>;
          case 'strong':
            return (
              <strong key={index} className="font-semibold text-heading">
                <InlineText nodes={node.children} />
              </strong>
            );
          case 'em':
            return (
              <em key={index}>
                <InlineText nodes={node.children} />
              </em>
            );
          case 'code':
            return (
              <code
                key={index}
                className="rounded bg-surface-2 px-1 py-0.5 font-mono text-[0.85em] text-heading"
              >
                {node.text}
              </code>
            );
          case 'link':
            return (
              <a
                key={index}
                href={node.href}
                target="_blank"
                rel="noopener noreferrer nofollow"
                className="text-accent underline underline-offset-2 hover:text-accent-hover"
              >
                {node.text}
              </a>
            );
        }
      })}
    </>
  );
}

const HEADING_CLASS = {
  1: 'text-base font-semibold text-heading',
  2: 'text-sm font-semibold text-heading',
  3: 'text-sm font-medium text-heading',
} as const;

const ALIGN_CLASS: Record<Exclude<Align, null>, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

function BlockView({ block }: { block: Block }) {
  switch (block.kind) {
    case 'code':
      return (
        <ScrollArea className="rounded-control border border-line bg-surface-2"><pre className="p-3 font-mono text-xs leading-relaxed text-body">
          <code>{block.text}</code>
        </pre></ScrollArea>
      );
    case 'table':
      return (
        <ScrollArea className="rounded-control border border-line">
          <table className="w-full border-collapse text-left text-sm">
            <thead className="bg-surface-2">
              <tr>
                {block.header.map((cell, column) => (
                  <th
                    key={column}
                    className={cn(
                      'border-b border-line px-3 py-2 text-xs font-semibold text-heading',
                      ALIGN_CLASS[block.align[column] ?? 'left'],
                    )}
                  >
                    <InlineText nodes={cell} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, rowIndex) => (
                <tr key={rowIndex} className="border-b border-line last:border-b-0 align-top">
                  {row.map((cell, column) => (
                    <td
                      key={column}
                      className={cn('px-3 py-2', ALIGN_CLASS[block.align[column] ?? 'left'])}
                    >
                      <InlineText nodes={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </ScrollArea>
      );
    case 'heading': {
      const Tag = (`h${block.level + 1}`) as 'h2' | 'h3' | 'h4';
      return (
        <Tag className={cn('pt-2', HEADING_CLASS[block.level])}>
          <InlineText nodes={block.content} />
        </Tag>
      );
    }
    case 'paragraph':
      return (
        <p>
          <InlineText nodes={block.content} />
        </p>
      );
    case 'quote':
      return (
        <blockquote className="border-l-2 border-line pl-3 text-muted">
          <InlineText nodes={block.content} />
        </blockquote>
      );
    case 'rule':
      return <hr className="border-line" />;
    case 'list': {
      const items = block.items.map((item, index) => (
        <li key={index}>
          <InlineText nodes={item} />
        </li>
      ));
      return block.ordered ? (
        <ol start={block.start} className="list-decimal space-y-1 pl-5">
          {items}
        </ol>
      ) : (
        <ul className="list-disc space-y-1 pl-5">{items}</ul>
      );
    }
  }
}

export interface DocumentTextProps {
  content: string;
  /** `markdown` (the default) or `text`, shown exactly as written. */
  format?: string;
  /** `legal` (the default) — what the mobile app also reads — or `guide`. */
  dialect?: Dialect;
  className?: string;
}

export function DocumentText({
  content,
  format = 'markdown',
  dialect = 'legal',
  className,
}: DocumentTextProps) {
  const blocks = useMemo(
    () => (format === 'markdown' ? parseDocument(content, dialect) : null),
    [content, format, dialect],
  );
  const base = cn('space-y-3 text-sm leading-relaxed text-body break-words', className);
  if (blocks === null) return <div className={cn(base, 'whitespace-pre-wrap')}>{content}</div>;
  return (
    <div className={base}>
      {blocks.map((block, index) => (
        <BlockView key={index} block={block} />
      ))}
    </div>
  );
}
